import React, { useEffect, useState } from 'react';
import './OakStatusBadge.css';

// Oak Status compliance award. Aggregate "compliance health" visual
// that grows from an acorn to a full oak as a supplier's required
// compliance documents are approved. Reaches "Oak Status" – vibrant
// Fusion-Green full oak – only at 100% (approved === required).
//
// Brief (2026-06):
//   - Inputs: required + approved counts.
//   - Frame library: 5 illustrations indexed 0..4
//       0 acorn         (none approved / dormant)
//       1 first shoot   (started)
//       2 seedling      (early growth)
//       3 young oak     (almost there)
//       4 full oak      (Oak Status – 100%)
//   - Default mapping: frame = round((approved / required) * 4),
//     CLAMPED so frame 4 is reserved for approved === required.
//   - In-app only – never embed in an email (existing rule).
//   - Restrained on the supplier portal – illustration, not mascot.
//   - Grow animation on advance, ~0.65s with spring overshoot;
//     CSS handles prefers-reduced-motion.
//
// Naming (the badge ITSELF, not consumers):
//   - 0 approved      → "Acorn" / "Not started"
//   - intermediate    → no named tier – caller shows raw progress
//                       ("3 of 5 documents approved · 60%")
//   - 100%            → "Oak Status"
//
// Colour:
//   - Foliage: Fusion-Green (#92D400) with darker tints for depth.
//   - Full oak: vibrant Fusion-Green (positive outcome – NOT
//     active-green; active-green is for "you are here" state).
//   - Dormant: natural browns (off-palette illustration shades).
//
// Props:
//   approved      number (default 0) – count of approved docs
//   required      number (default 0) – total required docs
//   size          number (default 84) – width in px; height auto
//                                       matches the 84:96 viewBox
//   animate       bool (default true) – fire the grow CSS animation
//                                       on each frame advance; pass
//                                       false to skip (e.g. in list
//                                       rows where the badge renders
//                                       in bulk and animation noise
//                                       would distract)
//   className     string – extra wrapper classes (e.g. for layout)

// ---- Frame index calc ----
function frameIndex(approved, required) {
  if (required <= 0) return 0;
  // 6-frame library indexed 0..5; frame = round(progress × 5) so every
  // approval steps the oak on visibly for all counts 1..5. Frame 5 (full
  // oak / Oak Status) is reserved strictly for 100%, frame 0 (acorn) for
  // 0 approved; intermediates are held to 1..4.
  if (approved >= required) return 5;
  if (approved <= 0) return 0;
  const partial = Math.round((approved / required) * 5);
  return Math.max(1, Math.min(4, partial));
}

// Status copy + pill tone for the optional adjacent label. Callers
// can reuse this via OakStatusBadge.statusFor(approved, required).
export function statusFor(approved, required) {
  if (required <= 0)              return { tier: 'acorn',   label: 'Acorn',      tone: 'grey'  };
  if (approved <= 0)              return { tier: 'acorn',   label: 'Acorn',      tone: 'grey'  };
  if (approved >= required)       return { tier: 'oak',     label: 'Oak Status', tone: 'green' };
  // Intermediate stages deliberately don't get named tiers (the
  // number of them varies by required count). Caller surfaces the
  // raw progress instead.
  return { tier: 'growing', label: null, tone: 'amber' };
}

// ---- SVG frame library ----
// All five frames share an 84×96 viewBox + ground shadow at
// (42, 90) so consumers can size with a single `size` prop and the
// transform-origin in CSS (50% 92%) lines up with the plant base.
//
// Colours hard-coded rather than read from CSS vars because the
// badge needs to render correctly on BOTH the admin dark canvas
// and the supplier portal light canvas – illustration shading is
// consistent across surfaces; only the wrapper bg changes.
const COLOURS = {
  shadow:    'rgba(0,0,0,0.35)',
  nut:       '#9a6633',
  nutDark:   '#5e3d1f',
  bark:      '#7a5230',
  leaf:      '#92D400',  // Fusion-Green
  leafMid:   '#6fa300',
  leafDeep:  '#4f7300',
};

function FrameAcorn() {
  return (
    <>
      <ellipse cx="42" cy="90" rx="18" ry="3" fill={COLOURS.shadow} />
      <path d="M28 60 Q28 88 42 88 Q56 88 56 60 Z" fill={COLOURS.nut} />
      <path d="M25 60 Q42 47 59 60 Q57 67 42 67 Q27 67 25 60 Z" fill={COLOURS.nutDark} />
      <rect x="40.5" y="42" width="3" height="9" rx="1.5" fill={COLOURS.nutDark} />
    </>
  );
}
function FrameFirstShoot() {
  return (
    <>
      <ellipse cx="42" cy="90" rx="18" ry="3" fill={COLOURS.shadow} />
      <path d="M30 64 Q30 88 42 88 Q54 88 54 64 Z" fill={COLOURS.nut} />
      <path d="M27 64 Q42 53 57 64 Q55 70 42 70 Q29 70 27 64 Z" fill={COLOURS.nutDark} />
      <path d="M42 56 q-2 -10 3 -16" stroke={COLOURS.leafMid} strokeWidth="3.4" fill="none" strokeLinecap="round" />
      <ellipse cx="47" cy="39" rx="6" ry="3.4" fill={COLOURS.leaf} transform="rotate(28 47 39)" />
    </>
  );
}
function FrameSeedling() {
  return (
    <>
      <ellipse cx="42" cy="90" rx="18" ry="3" fill={COLOURS.shadow} />
      <path d="M34 78 Q34 88 42 88 Q50 88 50 78 Z" fill={COLOURS.nut} />
      <path d="M42 80 V46" stroke={COLOURS.leafDeep} strokeWidth="3.4" strokeLinecap="round" />
      <ellipse cx="30" cy="56" rx="9" ry="5" fill={COLOURS.leaf} transform="rotate(-28 30 56)" />
      <ellipse cx="54" cy="50" rx="9" ry="5" fill={COLOURS.leafMid} transform="rotate(28 54 50)" />
    </>
  );
}
function FrameYoungOak() {
  return (
    <>
      <ellipse cx="42" cy="90" rx="18" ry="3" fill={COLOURS.shadow} />
      <path d="M42 88 V40" stroke={COLOURS.bark} strokeWidth="4.5" strokeLinecap="round" />
      <ellipse cx="28" cy="58" rx="10" ry="5.5" fill={COLOURS.leafMid} transform="rotate(-26 28 58)" />
      <ellipse cx="56" cy="54" rx="10" ry="5.5" fill={COLOURS.leaf} transform="rotate(26 56 54)" />
      <circle cx="42" cy="40" r="15" fill={COLOURS.leaf} />
      <circle cx="31" cy="44" r="10" fill={COLOURS.leafMid} />
      <circle cx="53" cy="44" r="10" fill={COLOURS.leafMid} />
    </>
  );
}
function FrameMaturingOak() {
  return (
    <>
      <ellipse cx="42" cy="90" rx="19" ry="3.2" fill={COLOURS.shadow} />
      <path d="M42 88 V46" stroke={COLOURS.bark} strokeWidth="5.2" strokeLinecap="round" />
      <path d="M42 64 L34 57 M42 58 L50 51" stroke={COLOURS.bark} strokeWidth="3" strokeLinecap="round" />
      <circle cx="42" cy="38" r="17.5" fill={COLOURS.leaf} />
      <circle cx="28" cy="45" r="11.5" fill={COLOURS.leafMid} />
      <circle cx="56" cy="45" r="11.5" fill={COLOURS.leafMid} />
      <circle cx="36" cy="30" r="9" fill={COLOURS.leaf} />
      <circle cx="50" cy="32" r="8" fill={COLOURS.leaf} />
    </>
  );
}
function FrameFullOak() {
  return (
    <>
      <ellipse cx="42" cy="90" rx="20" ry="3.5" fill={COLOURS.shadow} />
      <path d="M42 88 V52" stroke={COLOURS.bark} strokeWidth="6" strokeLinecap="round" />
      <path d="M42 66 L33 58 M42 60 L51 52" stroke={COLOURS.bark} strokeWidth="3.5" strokeLinecap="round" />
      <circle cx="42" cy="36" r="20" fill={COLOURS.leaf} />
      <circle cx="26" cy="44" r="13" fill={COLOURS.leafMid} />
      <circle cx="58" cy="44" r="13" fill={COLOURS.leafMid} />
      <circle cx="34" cy="28" r="11" fill={COLOURS.leaf} />
      <circle cx="52" cy="30" r="10" fill={COLOURS.leaf} />
    </>
  );
}

const FRAMES = [FrameAcorn, FrameFirstShoot, FrameSeedling, FrameYoungOak, FrameMaturingOak, FrameFullOak];
const FRAME_LABELS = ['Acorn', 'First shoot', 'Seedling', 'Young oak', 'Maturing oak', 'Full oak'];
// Per-frame vertical nudge (viewBox units) so each stage's VISIBLE content is
// centred in the 84×96 box rather than sitting ground-anchored at the bottom.
// Small stages (acorn/shoot) need the biggest lift; the full oak barely any.
// Keeps the oak vertically aligned with its neighbours (header pills, card
// text) wherever it's placed.
const FRAME_CENTER_OFFSET = [-16, -14, -18, -9, -7, -5];

export default function OakStatusBadge({
  approved = 0,
  required = 0,
  frame = null,        // force a specific stage 0..4, bypassing the
                       // approved/required ratio. Used by the recognition
                       // Badge to map a tier directly onto a growth stage
                       // (e.g. Gold → full oak) where there are no doc
                       // counts to derive from.
  size = 84,
  animate = true,
  className = '',
}) {
  const idx = frame != null
    ? Math.max(0, Math.min(5, frame))
    : frameIndex(approved, required);
  const Frame = FRAMES[idx];

  // Trigger the grow animation each time the frame index changes –
  // tracked via a useState + useEffect pair so React remounts the
  // animated group via a key bump rather than relying on CSS
  // animation-name swap (which doesn't re-trigger on identical
  // animation-name updates).
  const [animKey, setAnimKey] = useState(idx);
  useEffect(() => { setAnimKey(idx); }, [idx]);

  const height = Math.round((size / 84) * 96);
  const status = statusFor(approved, required);
  // When the frame is forced (no doc counts to describe), label by the
  // growth stage alone; otherwise use the compliance-progress phrasing.
  const ariaLabel = frame != null
    ? `Recognition – ${FRAME_LABELS[idx]}`
    : status.label
      ? `Compliance growth – ${status.label}`
      : `Compliance growth – ${FRAME_LABELS[idx]}, ${approved} of ${required} approved`;

  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 84 96"
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      {/* Outer group centres the frame's content vertically; inner group
          owns the grow animation so the two transforms don't collide. */}
      <g transform={`translate(0, ${FRAME_CENTER_OFFSET[idx]})`}>
        <g
          key={animate ? animKey : 'static'}
          className={animate ? 'oak-badge-svg__grows' : undefined}
        >
          <Frame />
        </g>
      </g>
    </svg>
  );
}
